import React from 'react'
import {usePage} from '@salesforce/commerce-sdk-react'
import {Box} from '@chakra-ui/react'

import {usePageDesigner} from '_customHooks'
import {BannerPageRef} from '_types'

interface BannerPageProps {
    /** A single server-resolved banner page reference (pageId + aspectAttributes). */
    pageRef: BannerPageRef
}

/**
 * Renders ONE Page Designer banner page for the PDP.
 *
 * Why this is its own component: the number of banner pages is data-driven (0..N, and it changes if the
 * backend adds a new SFRA condition), while `usePage` is a hook and cannot be called in a variable-length
 * loop. By isolating a single `usePage` call per component, the parent can simply map over the list
 * (`refs.map(ref => <BannerPage ... />)`): each instance has a fixed, unconditional hook set (Rules of
 * Hooks satisfied), exactly one getPage request is made per real ref (no fixed cap, so no phantom slots,
 * no wasted traffic, no 404s), and adding a 5th/Nth condition requires zero PWA changes.
 *
 * Parallelism / SSR: each instance mounts its own React Query query with `enabled` guarding a present
 * pageId. React Query dispatches them concurrently — on SSR all are prefetched together (no serial
 * waterfall, no TTFB regression), on the client they hydrate/refetch in parallel. Render order is the
 * parent's map order, so the ordered backend list is preserved regardless of network completion order.
 *
 * See .kiro/specs/pdp-banner-page-designer/spec.md.
 */
const BannerPage = React.memo(({pageRef}: BannerPageProps) => {
    const {renderPageDesignerRegion} = usePageDesigner()

    const {data: page} = usePage(
        {
            parameters: {
                pageId: pageRef?.pageId,
                aspectAttributes: JSON.stringify(pageRef?.aspectAttributes ?? {}),
            },
        },
        {enabled: !!pageRef?.pageId},
    )

    if (!page?.regions?.length) return null

    return (
        <Box data-testid="pdp-banner-page" data-banner-page-id={pageRef.pageId} data-banner-source={pageRef.source}>
            {page.regions.map((region) => renderPageDesignerRegion({region, analyticsName: page.id}))}
        </Box>
    )
})

BannerPage.displayName = 'BannerPage'

export {BannerPage}
